import { client } from 'src/enviroment/api/axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Mutation, Query } from '../index'
import { ProjectsQueryParams } from '../../types/projects'
import { ProjectStage, StageSearchParams } from '../../types/stage'
import { useTenantStore } from '../../enviroment/store/tenentStore'
import { queryTool } from '../../enviroment/lib/utils'
import { DEFAULT_TAKE, STAGE_ORDER } from '../../enviroment/constants/constants'

export const stageRouterKeys = {
  stages: 'stage.stages',
  myProjectStages: 'stage.myProjectStages',
  projectStages: 'stage.projectStages',
} as const

export const stageRouter = {
  search: {
    useQuery: (params: StageSearchParams = {}, opts?: Query<ProjectStage[]>) => {
      const { query = '', applicableOnly } = params

      return useQuery<ProjectStage[]>({
        queryKey: [stageRouterKeys.stages, query],
        queryFn: async () => {
          return (
            await client.get<ProjectStage[]>('/stages', {
              params: {
                query: queryTool(query),
                applicableOnly,
              },
            })
          ).data
        },
        ...opts,
      })
    },
  },
  //NOTE: DEPRECATED, using projectStages instead
  myProjectStages: {
    useQuery: (params: ProjectsQueryParams, opts?: Query<ProjectStage[]>) => {
      const tenantId = useTenantStore((state) => state.currentTenant?.id)

      return useQuery<ProjectStage[]>({
        queryKey: [stageRouterKeys.myProjectStages, tenantId, params],
        queryFn: async () => {
          return (
            await client.get<ProjectStage[]>(`/tenant/${tenantId}/stages:my-projects`, {
              params,
            })
          ).data
        },
        enabled: Boolean(tenantId),
        ...opts,
      })
    },
  },
  create: {
    useMutation: (opts?: Mutation<string, ProjectStage>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (name: string) => {
          return (await client.post<ProjectStage>('/stages', { name })).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({
            queryKey: [stageRouterKeys.stages],
          })
          opts?.onSuccess?.(...args)
        },
        ...opts,
      })
    },
  },
  delete: {
    useMutation: (opts?: Mutation<string, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (stageId: string) => {
          await client.delete(`/stages/${stageId}`)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({
            queryKey: [stageRouterKeys.stages],
          })
          opts?.onSuccess?.(...args)
        },
        ...opts,
      })
    },
  },
  projectStages: {
    useQuery: (params: { take?: number } = {}, opts?: Query<ProjectStage[]>) => {
      const tenantId = useTenantStore((state) => state.currentTenant?.id)

      return useQuery<ProjectStage[]>({
        queryKey: [stageRouterKeys.projectStages, tenantId, params],
        queryFn: async () => {
          return (
            await client.get<ProjectStage[]>(`/tenants/${tenantId}/projects/stages`, {
              params: {
                take: params.take ?? DEFAULT_TAKE,
              },
            })
          ).data?.sort((a, b) => STAGE_ORDER.indexOf(a.name) - STAGE_ORDER.indexOf(b.name)) // Return sorted
        },
        enabled: Boolean(tenantId),
        ...opts,
      })
    },
  },
}
